import { useRef, useState, useCallback } from 'react'
import { observer } from 'mobx-react-lite'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Avatar, Box, CircularProgress, IconButton, LinearProgress, Tooltip, Typography,
} from '@mui/material'
import UploadFileIcon     from '@mui/icons-material/UploadFile'
import VisibilityIcon     from '@mui/icons-material/Visibility'
import DeleteIcon         from '@mui/icons-material/Delete'
import FolderOpenIcon     from '@mui/icons-material/FolderOpen'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import ImageIcon          from '@mui/icons-material/Image'
import PictureAsPdfIcon   from '@mui/icons-material/PictureAsPdf'
import TableChartIcon     from '@mui/icons-material/TableChart'
import { retentionApi, RetentionAttachment } from '../../lib/api/retentionApi'
import { DeleteDialog } from '../shared/DeleteDialog'
import { toast } from 'react-toastify'
import { themeColors } from '../../app/layout/theme'

const ACCEPTED = '.xls,.xlsx,.jpeg,.jpg,.png,.pdf,.doc,.docx,.txt,.csv'
const MAX_MB   = 20

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })
}

function avatarInitials(name: string) { return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() }
function avatarColor(name: string) {
  let h = 0; for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h)
  return ['#4B3082','#7b1fa2','#2e7d32','#f57c00','#c62828','#00838f'][Math.abs(h) % 6]
}

function fileIcon(contentType: string) {
  if (contentType.startsWith('image/'))                                                          return <ImageIcon          sx={{ fontSize: 18, color: themeColors.primary }} />
  if (contentType === 'application/pdf')                                                          return <PictureAsPdfIcon   sx={{ fontSize: 18, color: '#dc2626'          }} />
  if (contentType.includes('spreadsheet') || contentType.includes('excel') || contentType.includes('csv')) return <TableChartIcon sx={{ fontSize: 18, color: '#16a34a'           }} />
  return <InsertDriveFileIcon sx={{ fontSize: 18, color: themeColors.text.muted }} />
}

function DropZone({ onFiles, uploading, progress }: { onFiles: (files: File[]) => void; uploading: boolean; progress: number }) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setDragging(false)
    const files = Array.from(e.dataTransfer.files)
    if (files.length) onFiles(files)
  }, [onFiles])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (files.length) onFiles(files)
    e.target.value = ''
  }

  return (
    <Box onClick={() => !uploading && inputRef.current?.click()}
      onDragOver={e => { e.preventDefault(); setDragging(true) }}
      onDragLeave={() => setDragging(false)} onDrop={handleDrop}
      sx={{
        border: `1.5px dashed ${dragging ? themeColors.primary : '#D1C4E9'}`, borderRadius: '12px',
        bgcolor: dragging ? '#F3E5F5' : '#F9F7FF', p: 3, mb: 3,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5,
        cursor: uploading ? 'not-allowed' : 'pointer', transition: 'all 0.2s',
        '&:hover': !uploading ? { borderColor: themeColors.primary, bgcolor: '#F3E5F5' } : {},
      }}>
      <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: '#F3E5F5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: themeColors.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <UploadFileIcon sx={{ color: '#fff', fontSize: 16 }} />
        </Box>
      </Box>
      {uploading ? (
        <Box sx={{ width: '100%', maxWidth: 280, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 13, color: themeColors.text.secondary, mb: 1 }}>Uploading… {progress}%</Typography>
          <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 4, height: 6, bgcolor: '#EDE7F6', '& .MuiLinearProgress-bar': { bgcolor: themeColors.primary } }} />
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#444', mb: 0.5 }}>
            <span style={{ color: themeColors.primary, cursor: 'pointer' }}>Click</span> or <strong>Drag & Drop</strong> Files Here to Upload
          </Typography>
          <Typography sx={{ fontSize: '11px', color: themeColors.text.muted }}>
            Supported Format: XLS, JPEG, PNG, PDF &nbsp;•&nbsp; Max {MAX_MB} MB
          </Typography>
        </Box>
      )}
      <input ref={inputRef} type="file" multiple accept={ACCEPTED} style={{ display: 'none' }} onChange={handleChange} />
    </Box>
  )
}

function FilesTable({ attachments, canDelete, onDelete, onView }: {
  attachments: RetentionAttachment[]; canDelete: boolean; onDelete: (a: RetentionAttachment) => void; onView: (a: RetentionAttachment) => void
}) {
  const COL_HEAD = { fontSize: '12px', fontWeight: 600, color: themeColors.text.secondary, textTransform: 'none' as const }
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <FolderOpenIcon sx={{ fontSize: 20, color: '#555' }} />
        <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#333' }}>Files ({attachments.length})</Typography>
      </Box>
      <Box sx={{ border: `1px solid ${themeColors.border}`, borderRadius: '12px', overflow: 'hidden' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1fr 0.8fr 100px', px: 2.5, py: 1.5, bgcolor: '#F8F9FD', borderBottom: `1px solid ${themeColors.border}` }}>
          <Typography sx={COL_HEAD}>File Name</Typography>
          <Typography sx={COL_HEAD}>Attached By</Typography>
          <Typography sx={COL_HEAD}>Date</Typography>
          <Typography sx={COL_HEAD}>Size</Typography>
          <Typography sx={{ ...COL_HEAD, textAlign: 'right' }}>Actions</Typography>
        </Box>
        <Box>
          {attachments.map((a, idx) => (
            <Box key={a.id} sx={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1fr 0.8fr 100px', px: 2.5, py: 1.5, alignItems: 'center', borderBottom: idx === attachments.length - 1 ? 'none' : `1px solid ${themeColors.border}`, bgcolor: '#fff', '&:hover': { bgcolor: themeColors.surfaceHover }, transition: 'background 0.1s' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {fileIcon(a.contentType)}
                </Box>
                <Typography sx={{ fontSize: '13px', color: themeColors.text.primary, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.fileName}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {a.uploadedByName ? (
                  <>
                    <Avatar sx={{ width: 24, height: 24, fontSize: '10px', fontWeight: 700, bgcolor: avatarColor(a.uploadedByName), color: '#fff' }}>
                      {a.uploadedByAvatar
                        ? <img src={a.uploadedByAvatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : avatarInitials(a.uploadedByName)}
                    </Avatar>
                    <Typography sx={{ fontSize: '12.5px', color: themeColors.text.secondary }}>{a.uploadedByName}</Typography>
                  </>
                ) : (
                  <Typography sx={{ fontSize: '12.5px', color: themeColors.text.muted }}>—</Typography>
                )}
              </Box>
              <Typography sx={{ fontSize: '12.5px', color: themeColors.text.secondary }}>{formatDate(a.createdAt)}</Typography>
              <Typography sx={{ fontSize: '12.5px', color: themeColors.text.secondary }}>{formatSize(a.sizeBytes)}</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                <Tooltip title="View / Download">
                  <IconButton size="small" onClick={() => onView(a)} sx={{ color: themeColors.text.muted, '&:hover': { color: themeColors.primary } }}>
                    <VisibilityIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                {canDelete && (
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => onDelete(a)} sx={{ color: themeColors.text.muted, '&:hover': { color: themeColors.error } }}>
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

const RetentionAttachmentsSection = observer(function RetentionAttachmentsSection({ retentionId, canModify }: { retentionId: string; canModify: boolean }) {
  const qc = useQueryClient()
  const [uploadProgress, setUploadProgress] = useState(0)
  const [deleteTarget, setDeleteTarget] = useState<RetentionAttachment | null>(null)

  const { data: attachments = [], isLoading } = useQuery({
    queryKey: ['retention-attachments', retentionId],
    queryFn: () => retentionApi.getAttachments(retentionId),
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['retention-attachments', retentionId] })
    qc.invalidateQueries({ queryKey: ['retention-activities', retentionId] })
  }

  const uploadMut = useMutation({
    mutationFn: (file: File) => retentionApi.uploadAttachment(retentionId, file, pct => setUploadProgress(pct)),
    onSuccess: () => { invalidate(); setUploadProgress(0); toast.success('File uploaded') },
    onError: () => { setUploadProgress(0); toast.error('Failed to upload file') },
  })

  const deleteMut = useMutation({
    mutationFn: (attachmentId: string) => retentionApi.deleteAttachment(retentionId, attachmentId),
    onSuccess: () => { invalidate(); setDeleteTarget(null); toast.success('File deleted') },
    onError: () => toast.error('Failed to delete file'),
  })

  function handleFiles(files: File[]) {
    const file = files[0]
    if (!file) return
    if (file.size > MAX_MB * 1024 * 1024) { toast.error(`File exceeds ${MAX_MB} MB limit`); return }
    uploadMut.mutate(file)
  }

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={32} thickness={4} sx={{ color: themeColors.primary }} /></Box>

  return (
    <Box sx={{ p: 0.5 }}>
      {canModify && <DropZone onFiles={handleFiles} uploading={uploadMut.isPending} progress={uploadProgress} />}

      {attachments.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, border: `1px dashed ${themeColors.border}`, borderRadius: '12px', bgcolor: '#fafafa' }}>
          <FolderOpenIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1.5 }} />
          <Typography sx={{ color: themeColors.text.secondary, fontWeight: 600, fontSize: '15px' }}>No attachments yet</Typography>
          <Typography sx={{ color: themeColors.text.muted, fontSize: '13px', maxWidth: 280, mx: 'auto', mt: 0.5 }}>Upload contracts, invoices, or any relevant files</Typography>
        </Box>
      ) : (
        <FilesTable attachments={attachments} canDelete={canModify} onDelete={setDeleteTarget} onView={a => window.open(a.url, '_blank', 'noopener,noreferrer')} />
      )}

      <DeleteDialog
        open={deleteTarget !== null}
        title="Delete Attachment"
        name={deleteTarget?.fileName}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
        onClose={() => setDeleteTarget(null)}
        isDeleting={deleteMut.isPending}
      />
    </Box>
  )
})

export default RetentionAttachmentsSection
